import { useCallback, useEffect, useMemo, useState } from "react";
import { ArrowLeft, ExternalLink, Pencil, Plus, RefreshCw, Search, Trash2, X } from "lucide-react";
import { Bar, BarChart, ResponsiveContainer, Tooltip, XAxis, YAxis } from "recharts";
import { toast } from "sonner";
import AdminLayout from "@/admin/AdminLayout";
import AdminPageHelp from "@/design-system/components/AdminPageHelp";
import ConfirmDialog from "@/design-system/components/ConfirmDialog";
import { useAdminPermissions } from "@/admin/PermissionsContext";
import { api, formatApiError } from "@/api/client";
import { Section } from "@/admin/_primitives";

const FIELDS = {
  identity: [
    { key: "name", label: "Name", required: true },
    { key: "description", label: "Description", multiline: true },
    { key: "logo_url", label: "Logo URL", placeholder: "https://…" },
  ],
  eligibility: [
    { key: "min_credit_score", label: "Min credit score", type: "number" },
    { key: "min_monthly_revenue", label: "Min monthly revenue ($)", type: "number" },
    { key: "max_recent_late_payments_in_48mo", label: "Max late payments / 48mo", type: "number" },
    { key: "time_in_business_months_required", label: "Time in business (months)", type: "number" },
    { key: "bank_statement_months_required", label: "Bank-statement months req'd", type: "number" },
    { key: "business_bank_account_required", label: "Business bank account required", type: "bool" },
    { key: "bank_statements_required", label: "Bank statements required", type: "bool" },
    { key: "ein_required", label: "EIN required", type: "bool" },
    { key: "business_entity_required", label: "Business entity required", type: "bool" },
    { key: "consumer_eligible", label: "Consumer eligible", type: "bool" },
  ],
  funding: [
    { key: "min_funding_amount", label: "Min funding ($)", type: "number" },
    { key: "max_funding_amount", label: "Max funding ($)", type: "number" },
  ],
  application: [
    { key: "application_link", label: "Application link", required: true, placeholder: "https://…" },
    { key: "contact_email", label: "Contact email", type: "email" },
  ],
  operational: [
    { key: "internal_notes", label: "Internal notes", multiline: true },
    { key: "is_active", label: "Active", type: "bool" },
    { key: "sort_priority", label: "Sort priority", type: "number" },
  ],
};

const EMPTY_PARTNER = Object.fromEntries(
  Object.values(FIELDS).flat().map((f) => [
    f.key,
    f.type === "bool"
      ? (f.key === "is_active" || f.key === "consumer_eligible")
      : f.type === "number" ? "" : "",
  ])
);
EMPTY_PARTNER.sort_priority = 100;
// V1.1a — qualification_tips is rendered by its own editor, not the FIELDS grid,
// so seed an empty list here for newly-created partners.
EMPTY_PARTNER.qualification_tips = [];

function fmtMoney(n) {
  if (n == null || n === "") return "—";
  return `$${Number(n).toLocaleString()}`;
}

export default function AdminPartners() {
  const { has } = useAdminPermissions();
  const canEdit = has("edit_funding_partners");
  const [items, setItems] = useState([]);
  const [clicks, setClicks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [activeFilter, setActiveFilter] = useState("all");
  const [editing, setEditing] = useState(null); // null | "new" | partnerDoc
  const [recomputing, setRecomputing] = useState(false);
  const [confirmingDelete, setConfirmingDelete] = useState(null); // null | partner
  const [deleteBusy, setDeleteBusy] = useState(false);

  const reload = useCallback(async () => {
    setLoading(true);
    try {
      const [partners, clicksRes] = await Promise.all([
        api.get("/admin/partners"),
        api.get("/admin/partner-application-clicks?limit=500").catch(() => ({ data: [] })),
      ]);
      setItems(partners.data || []);
      setClicks(clicksRes.data || []);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { reload(); }, [reload]);

  const filtered = useMemo(() => {
    const term = q.trim().toLowerCase();
    return items.filter((p) => {
      if (term && !`${p.name} ${p.description || ""}`.toLowerCase().includes(term)) return false;
      if (activeFilter === "active" && !p.is_active) return false;
      if (activeFilter === "inactive" && p.is_active) return false;
      return true;
    });
  }, [items, q, activeFilter]);

  const clickChartData = useMemo(() => {
    // Aggregate clicks by partner over the last 30 days.
    const now = Date.now();
    const cutoff = now - 30 * 24 * 60 * 60 * 1000;
    const counts = new Map();
    for (const c of clicks) {
      const ts = c.clicked_at ? new Date(c.clicked_at).getTime() : 0;
      if (ts < cutoff) continue;
      const name = c.partner_name_snapshot || c.partner_id || "(unknown)";
      counts.set(name, (counts.get(name) || 0) + 1);
    }
    return Array.from(counts.entries())
      .map(([name, count]) => ({ name, count }))
      .sort((a, b) => b.count - a.count)
      .slice(0, 10);
  }, [clicks]);

  async function handleToggleActive(partner) {
    try {
      await api.patch(`/admin/partners/${partner.id || partner._id}`, { is_active: !partner.is_active });
      toast.success(partner.is_active ? "Partner deactivated" : "Partner activated");
      reload();
    } catch (e) { toast.error(formatApiError(e)); }
  }

  async function handleDelete(partner) {
    setDeleteBusy(true);
    try {
      await api.delete(`/admin/partners/${partner.id || partner._id}`);
      toast.success("Partner deleted");
      setConfirmingDelete(null);
      reload();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setDeleteBusy(false);
    }
  }

  async function handleRecompute() {
    setRecomputing(true);
    try {
      const { data } = await api.post("/admin/partners/recompute-all-matches");
      toast.success(`Recomputed: ${data.invalidated} user match cache(s) invalidated`);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setRecomputing(false); }
  }

  return (
    <AdminLayout
      title="Partners"
      subtitle={`${items.length} funding partner${items.length === 1 ? "" : "s"}`}
      actions={
        canEdit && (
          <div className="flex items-center gap-2">
            <button
              type="button"
              data-testid="partners-recompute"
              onClick={handleRecompute}
              disabled={recomputing}
              className="inline-flex items-center gap-1.5 rounded-md border border-[var(--v2-border)] px-3 py-1.5 text-xs text-[var(--v2-text-muted)] hover:text-[var(--v2-text)] disabled:opacity-60"
            >
              <RefreshCw className={`w-3 h-3 ${recomputing ? "animate-spin" : ""}`} /> Recompute all matches
            </button>
            <button
              type="button"
              data-testid="partners-new"
              onClick={() => setEditing("new")}
              className="inline-flex items-center gap-1.5 rounded-md px-3.5 py-1.5 text-sm font-medium"
              style={{ background: "#B794F4", color: "var(--v2-accent-ink)" }}
            >
              <Plus className="w-3.5 h-3.5" /> New partner
            </button>
          </div>
        )
      }
    >
      <AdminPageHelp
        title="Partners"
        purpose="Manages the vetted-lender catalog used by the Funding Partner Match engine. Each row is one funding partner shown to eligible users."
        configures="Partner name, description, hero URL, eligibility criteria (revenue floor, credit-score floor, entity type, TIB months), match priority, and 'is_active' flag."
        impact="Live: partners the user sees at /partners refresh on next page load. 'Recompute all matches' regenerates every user's cached match set — expensive, use sparingly."
        safety="Yes for row edits — toggle is_active off to hide without deleting. Deleting a partner permanently removes its row (historical match records remain in analytics)."
      />
      {/* Filters */}
      <div className="grid md:grid-cols-[1fr_auto] gap-3 mb-4">
        <div className="relative">
          <Search className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[var(--v2-text-muted)]" />
          <input
            type="text"
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search partners by name or description…"
            data-testid="partners-search"
            className="w-full rounded-md border border-[var(--v2-border)] bg-[var(--v2-surface)] pl-9 pr-3 py-2 text-sm text-[var(--v2-text)] placeholder:text-[var(--v2-text-muted)] focus:outline-none focus:border-[#B794F4]"
          />
        </div>
        <select
          value={activeFilter}
          onChange={(e) => setActiveFilter(e.target.value)}
          data-testid="partners-status-filter"
          aria-label="Filter by status"
          className="rounded-md border border-[var(--v2-border)] bg-[var(--v2-surface)] px-2 py-2 text-xs text-[var(--v2-text)]"
        >
          <option value="all">All</option>
          <option value="active">Active only</option>
          <option value="inactive">Inactive only</option>
        </select>
      </div>

      {loading ? (
        <div className="text-sm text-[var(--v2-text-muted)]">Loading…</div>
      ) : (
        <>
          <div data-testid="partners-table" className="rounded-xl border border-[var(--v2-border)] overflow-hidden" style={{ background: "var(--v2-surface)" }}>
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-[0.16em] text-[var(--v2-text-muted)] border-b border-[var(--v2-border)]">
                <tr>
                  <th className="text-left px-3 py-2.5 font-normal">Name</th>
                  <th className="text-left px-3 py-2.5 font-normal">Status</th>
                  <th className="text-left px-3 py-2.5 font-normal">Funding</th>
                  <th className="text-left px-3 py-2.5 font-normal">Min score</th>
                  <th className="text-left px-3 py-2.5 font-normal">Min rev</th>
                  <th className="text-left px-3 py-2.5 font-normal">Priority</th>
                  <th className="text-left px-3 py-2.5 font-normal">Updated</th>
                  <th className="text-right px-3 py-2.5 font-normal">Actions</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((p) => (
                  <tr key={p.id || p._id} data-testid={`partners-row-${p.id || p._id}`} className="border-b border-[var(--v2-border)] hover:bg-[rgba(183,148,244,0.04)] cursor-pointer" onClick={() => canEdit && setEditing(p)}>
                    <td className="px-3 py-2.5">
                      <div className="text-[var(--v2-text)]">{p.name}</div>
                      <div className="text-[11px] text-[var(--v2-text-muted)] truncate max-w-[280px]">{p.description}</div>
                    </td>
                    <td className="px-3 py-2.5">
                      <button
                        type="button"
                        data-testid={`partners-toggle-${p.id || p._id}`}
                        disabled={!canEdit}
                        onClick={(e) => { e.stopPropagation(); handleToggleActive(p); }}
                        className="text-[11px] rounded-full px-2 py-0.5 border"
                        style={
                          p.is_active
                            ? { color: "#86EFAC", borderColor: "rgba(74,222,128,0.35)", background: "rgba(74,222,128,0.08)" }
                            : { color: "#FCA5A5", borderColor: "rgba(220,38,38,0.35)", background: "rgba(220,38,38,0.06)" }
                        }
                      >
                        {p.is_active ? "Active" : "Inactive"}
                      </button>
                    </td>
                    <td className="px-3 py-2.5 text-[var(--v2-text-muted)] whitespace-nowrap">
                      {fmtMoney(p.min_funding_amount)} – {fmtMoney(p.max_funding_amount)}
                    </td>
                    <td className="px-3 py-2.5 tabular-nums">{p.min_credit_score ?? "—"}</td>
                    <td className="px-3 py-2.5 tabular-nums">{fmtMoney(p.min_monthly_revenue)}</td>
                    <td className="px-3 py-2.5 tabular-nums">{p.sort_priority ?? 100}</td>
                    <td className="px-3 py-2.5 text-[var(--v2-text-muted)] text-xs">{p.updated_at?.slice(0, 10)}</td>
                    <td className="px-3 py-2.5">
                      <div className="flex items-center justify-end gap-2">
                        {canEdit && (
                          <>
                            <button
                              type="button"
                              data-testid={`partners-edit-${p.id || p._id}`}
                              onClick={(e) => { e.stopPropagation(); setEditing(p); }}
                              className="text-[#B794F4] text-xs hover:underline inline-flex items-center gap-1"
                            ><Pencil className="w-3 h-3" /> Edit</button>
                            <button
                              type="button"
                              data-testid={`partners-delete-${p.id || p._id}`}
                              onClick={(e) => { e.stopPropagation(); setConfirmingDelete(p); }}
                              className="text-[#FCA5A5] text-xs hover:underline inline-flex items-center gap-1"
                            ><Trash2 className="w-3 h-3" /> Delete</button>
                          </>
                        )}
                      </div>
                    </td>
                  </tr>
                ))}
                {filtered.length === 0 && (
                  <tr><td colSpan="8" className="text-center text-[var(--v2-text-muted)] py-8 text-sm">No partners match.</td></tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Clicks chart */}
          <div data-testid="partners-clicks-chart" className="mt-6 rounded-xl border border-[var(--v2-border)] p-5" style={{ background: "var(--v2-surface)" }}>
            <div className="flex items-center justify-between mb-3">
              <div>
                <h3 className="text-[10px] uppercase tracking-[0.18em] text-[var(--v2-text-muted)]">Top 10 partners by clicks (last 30 days)</h3>
                <p className="text-sm text-[var(--v2-text-muted)] mt-1">Total: {clicks.length} clicks recorded</p>
              </div>
            </div>
            {clickChartData.length === 0 ? (
              <p className="text-sm text-[var(--v2-text-muted)] py-6 text-center">No clicks recorded in the last 30 days.</p>
            ) : (
              <div className="h-64">
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart data={clickChartData} margin={{ top: 8, right: 8, left: 8, bottom: 36 }}>
                    <XAxis dataKey="name" angle={-25} textAnchor="end" stroke="var(--ds-color-ink-subtle)" tick={{ fontSize: 11 }} interval={0} />
                    <YAxis stroke="var(--ds-color-ink-subtle)" tick={{ fontSize: 11 }} allowDecimals={false} />
                    <Tooltip contentStyle={{ background: "var(--ds-color-surface)", border: "1px solid var(--ds-color-admin-border)", borderRadius: "var(--ds-radius-input)", fontSize: 12, color: "var(--ds-color-ink)" }} cursor={{ fill: "var(--ds-color-admin-surface-muted)" }} />
                    <Bar dataKey="count" fill="var(--ds-color-accent-strong)" radius={[4, 4, 0, 0]} />
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </div>
        </>
      )}

      {editing && (
        <PartnerModal
          partner={editing === "new" ? { ...EMPTY_PARTNER } : editing}
          isNew={editing === "new"}
          onClose={() => setEditing(null)}
          onSaved={() => { setEditing(null); reload(); }}
        />
      )}

      <ConfirmDialog
        open={!!confirmingDelete}
        onCancel={() => setConfirmingDelete(null)}
        onConfirm={() => confirmingDelete && handleDelete(confirmingDelete)}
        busy={deleteBusy}
        variant="destructive"
        title={confirmingDelete ? `Delete ${confirmingDelete.name}?` : "Delete partner"}
        description={
          <>
            <p>
              Deleting this partner immediately removes it from every user's match view and
              recomputes matches for all users.
            </p>
            <p className="mt-2" style={{ color: "var(--v2-text-muted)" }}>
              Historical click and match records remain in analytics.
            </p>
          </>
        }
        confirmLabel="Delete partner"
        testId="partners-delete-confirm"
      />
    </AdminLayout>
  );
}

function PartnerModal({ partner, isNew, onClose, onSaved }) {
  const [form, setForm] = useState(() => ({ ...partner }));
  const [busy, setBusy] = useState(false);

  function update(k, v) { setForm((f) => ({ ...f, [k]: v })); }

  async function handleSave(e) {
    e?.preventDefault?.();
    setBusy(true);
    try {
      const payload = { ...form };
      // Coerce numbers
      for (const f of Object.values(FIELDS).flat()) {
        if (f.type === "number") {
          const v = payload[f.key];
          payload[f.key] = v === "" || v == null ? null : Number(v);
        }
      }
      // V1.1a — drop empty / whitespace-only qualification tips before save.
      if (Array.isArray(payload.qualification_tips)) {
        payload.qualification_tips = payload.qualification_tips
          .map((t) => (t || "").trim())
          .filter(Boolean);
      }
      if (isNew) {
        await api.post("/admin/partners", payload);
        toast.success("Partner created");
      } else {
        await api.patch(`/admin/partners/${partner.id || partner._id}`, payload);
        toast.success("Partner updated");
      }
      onSaved();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div data-testid="partners-modal" className="fixed inset-0 z-50 flex items-center justify-center p-4" style={{ background: "rgba(0,0,0,0.65)" }} onClick={onClose}>
      <div className="w-full max-w-3xl max-h-[90vh] overflow-y-auto rounded-xl border border-[var(--v2-border)] p-6" style={{ background: "var(--v2-surface)" }} onClick={(e) => e.stopPropagation()}>
        <div className="flex items-start justify-between mb-5">
          <div className="flex items-center gap-3 min-w-0">
            <button
              type="button"
              onClick={onClose}
              data-testid="partners-modal-back"
              className="inline-flex items-center gap-1 text-xs text-[var(--v2-text-muted)] hover:text-[var(--v2-text)] transition-colors"
            >
              <ArrowLeft className="w-3.5 h-3.5" /> Back to list
            </button>
            <div className="min-w-0">
              <div className="text-[10px] uppercase tracking-[0.18em] text-[#B794F4]">Funding partner</div>
              <h2 className="font-display text-xl text-[var(--v2-text)] mt-1 truncate">{isNew ? "Create partner" : form.name}</h2>
            </div>
          </div>
          <button data-testid="partners-modal-close" onClick={onClose} className="text-[var(--v2-text-muted)] hover:text-[var(--v2-text)]">
            <X className="w-4 h-4" />
          </button>
        </div>

        <form onSubmit={handleSave} className="space-y-6">
          {Object.entries(FIELDS).map(([section, fields]) => (
            <Section key={section} title={section}>
              <div className="grid sm:grid-cols-2 gap-3">
                {fields.map((field) => (
                  <FieldInput key={field.key} field={field} value={form[field.key]} onChange={(v) => update(field.key, v)} />
                ))}
              </div>
              {section === "application" && form.application_link && (
                <button type="button" onClick={() => window.open(form.application_link, "_blank")} className="mt-2 text-xs text-[#B794F4] hover:underline inline-flex items-center gap-1">
                  <ExternalLink className="w-3 h-3" /> Test link
                </button>
              )}
            </Section>
          ))}

          {/* V1.1a — qualification_tips editor (admin-managed list shown in the
              consumer "How to qualify" accordion on /partners). */}
          <Section title="qualification tips">
            <QualificationTipsEditor
              tips={form.qualification_tips || []}
              onChange={(next) => update("qualification_tips", next)}
            />
          </Section>

          <div className="flex items-center justify-end gap-2 pt-2 border-t border-[var(--v2-border)]">
            <button type="button" onClick={onClose} className="rounded-md border border-[var(--v2-border)] px-3 py-1.5 text-sm text-[var(--v2-text-muted)] hover:text-[var(--v2-text)]">Cancel</button>
            <button type="submit" disabled={busy} data-testid="partners-modal-save" className="rounded-md px-4 py-1.5 text-sm font-medium disabled:opacity-60" style={{ background: "#B794F4", color: "var(--v2-accent-ink)" }}>
              {busy ? "Saving…" : isNew ? "Create partner" : "Save changes"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}


function FieldInput({ field, value, onChange }) {
  if (field.type === "bool") {
    return (
      <label className="flex items-center gap-2 px-3 py-2 rounded-md border border-[var(--v2-border)]">
        <input
          type="checkbox"
          checked={!!value}
          onChange={(e) => onChange(e.target.checked)}
          data-testid={`partners-field-${field.key}`}
          className="accent-[#B794F4]"
        />
        <span className="text-sm text-[var(--v2-text-muted)]">{field.label}</span>
      </label>
    );
  }
  if (field.multiline) {
    return (
      <label className="sm:col-span-2 text-xs text-[var(--v2-text-muted)]">
        <span className="block mb-1 uppercase tracking-[0.12em] text-[10px]">{field.label}</span>
        <textarea
          value={value ?? ""}
          onChange={(e) => onChange(e.target.value)}
          rows={field.key === "internal_notes" ? 3 : 2}
          data-testid={`partners-field-${field.key}`}
          className="w-full rounded-md border border-[var(--v2-border)] bg-transparent px-3 py-2 text-sm text-[var(--v2-text)] focus:outline-none focus:border-[#B794F4]"
        />
      </label>
    );
  }
  return (
    <label className="text-xs text-[var(--v2-text-muted)]">
      <span className="block mb-1 uppercase tracking-[0.12em] text-[10px]">{field.label}{field.required ? " *" : ""}</span>
      <input
        type={field.type === "number" ? "number" : field.type || "text"}
        required={field.required}
        value={value ?? ""}
        onChange={(e) => onChange(e.target.value)}
        placeholder={field.placeholder}
        data-testid={`partners-field-${field.key}`}
        className="w-full rounded-md border border-[var(--v2-border)] bg-transparent px-3 py-2 text-sm text-[var(--v2-text)] focus:outline-none focus:border-[#B794F4]"
      />
    </label>
  );
}

// V1.1a — admin-editable list of "How to qualify" tips per partner.
// Render each tip as its own input row with add / remove controls. The
// underlying value stays a `List[str]` so the public match payload echoes
// the array unchanged.
function QualificationTipsEditor({ tips, onChange }) {
  const items = Array.isArray(tips) ? tips : [];

  function setAt(index, value) {
    const next = [...items];
    next[index] = value;
    onChange(next);
  }
  function add() {
    onChange([...items, ""]);
  }
  function remove(index) {
    const next = items.filter((_, i) => i !== index);
    onChange(next);
  }

  return (
    <div data-testid="partners-field-qualification-tips" className="space-y-2">
      <p className="text-[11px] text-[var(--v2-text-muted)] leading-relaxed">
        Surfaced inside the consumer "How to qualify" accordion on <code>/partners</code>.
        Keep tips short, action-oriented, and educational — never make guarantees.
      </p>
      {items.length === 0 && (
        <p data-testid="partners-tips-empty" className="text-xs text-[var(--v2-text-muted)] italic">No tips yet.</p>
      )}
      {items.map((tip, i) => (
        <div key={i} className="flex gap-2 items-start">
          <textarea
            value={tip}
            onChange={(e) => setAt(i, e.target.value)}
            rows={2}
            data-testid={`partners-tip-input-${i}`}
            placeholder="e.g. Maintain 70%+ deposit consistency over 3 months."
            className="flex-1 rounded-md border border-[var(--v2-border)] bg-transparent px-3 py-2 text-sm text-[var(--v2-text)] focus:outline-none focus:border-[#B794F4]"
          />
          <button
            type="button"
            onClick={() => remove(i)}
            data-testid={`partners-tip-remove-${i}`}
            className="mt-1 inline-flex items-center justify-center w-7 h-7 rounded border border-[var(--v2-border)] text-[var(--v2-text-muted)] hover:text-[#FCA5A5] hover:border-[rgba(252,165,165,0.4)]"
            aria-label="Remove tip"
          >
            <Trash2 className="w-3 h-3" />
          </button>
        </div>
      ))}
      <button
        type="button"
        onClick={add}
        data-testid="partners-tip-add"
        className="inline-flex items-center gap-1.5 rounded-md border border-dashed border-[var(--v2-border)] px-3 py-1.5 text-xs text-[var(--v2-text-muted)] hover:text-[var(--v2-text)] hover:border-[#B794F4]"
      >
        <Plus className="w-3 h-3" /> Add tip
      </button>
    </div>
  );
}

